import type { ReactNode } from "react";
import type { PickerOverlaySurface } from "@omega/ui";

/**
 * Контекст render опции в overlay list. \
 * `surface` уже выбран слотом — в `popover`-слоте всегда `"popover"`.
 */
export type OptionSurfaceRenderContext = {
    selected: boolean;
    disabled: boolean;
    surface: PickerOverlaySurface;
};

/**
 * Точечные overrides рендера опции по surface. \
 * Незаданный слот не затирает default нижележащего слоя.
 */
export type OptionSurfaceSlots<T> = {
    /**
     * Полный render строки в popover. \
     * Если нет — default chrome + text content.
     */
    popover?: (option: T, ctx: OptionSurfaceRenderContext) => ReactNode;
    /**
     * Полный render строки в fullscreen. \
     * Если нет — default chrome + text content (не копирует `popover`).
     */
    fullscreen?: (option: T, ctx: OptionSurfaceRenderContext) => ReactNode;
};

type OptionSurfaceDefaults<T> = {
    popover: (option: T, ctx: OptionSurfaceRenderContext) => ReactNode;
    fullscreen: (option: T, ctx: OptionSurfaceRenderContext) => ReactNode;
};

/**
 * Выбирает renderer для surface: consumer slot → default того же surface. \
 * `optionSlots.popover` **не** подставляется в fullscreen.
 * @param surface resolved overlay surface
 * @param optionSlots consumer overrides
 * @param defaults chrome/defaults текущего слоя
 */
export function resolveOptionSurfaceRender<T>(
    surface: PickerOverlaySurface,
    optionSlots: OptionSurfaceSlots<T> | undefined,
    defaults: OptionSurfaceDefaults<T>,
): (option: T, ctx: OptionSurfaceRenderContext) => ReactNode {
    if (surface === "fullscreen") {
        return optionSlots?.fullscreen ?? defaults.fullscreen;
    }
    return optionSlots?.popover ?? defaults.popover;
}
